import { isBefore, isAfter, endOfDay } from 'date-fns'
import { IOrderItem } from '../../model/order.store'
import { head, filter, map, cloneDeep, reduce, sumBy, includes, orderBy } from 'lodash'
import { WOMEN_DAY_GIFT_SKUS_MAP, WomenDaysGiftType } from '../skus.utils'
import { WOMEN_8T3_MENUS } from './cake-83'
import { getDeliverĐeadline } from './discount-utils'
import { getItemSoldPrice } from '../order.util'

export const WOMEN_8T3_TIME_FRAME = {
  start: new Date('2026-02-23T00:00:00+07:00'),
  end: new Date('2026-03-09T00:00:00+07:00') // Đến hết 8/3
}
/**
 * Chương trình tặng quà ngày Quốc tế Phụ nữ 8/3
 * Quà tặng:
 * - card: Thiệp 8/3 (mẫu ngẫu nhiên)
 * - sparkle: Nến pháo bông tăm
 */
export function checkAndGetValidGift8T3(
  items: IOrderItem[],
  onlineOrder: any,
  giftType: WomenDaysGiftType
) {
  const deliverDeadline = getDeliverĐeadline(onlineOrder)
  let validGiftingSaleSkus: string[] = []
  let validGiftingSaleProducts: any[] = []
  let giftDesc = ''
  let discountNum = 0

  const giftSku = head(WOMEN_DAY_GIFT_SKUS_MAP[giftType])
  if (giftType === 'card') {
    validGiftingSaleSkus = map(
      filter(WOMEN_8T3_MENUS, item => item.giftName === 'card'),
      'sku'
    )
    giftDesc = 'Thiệp 8/3 (mẫu ngẫu nhiên)'
    discountNum = sumBy(items, item => {
      if (validGiftingSaleSkus.includes(item.sku)) {
        return item.quantity
      }
      return 0
    })
  }

  if (giftType === 'sparkle') {
    validGiftingSaleProducts = filter(WOMEN_8T3_MENUS, item => item.giftName === 'sparkle')
    validGiftingSaleSkus = map(validGiftingSaleProducts, 'sku')
    giftDesc = 'Nến pháo bông tăm'
    const cloneItems = cloneDeep(items)
    const cumulatedItems = Object.values(
      reduce(
        cloneItems,
        (result, item) => {
          result[item.sku] = result[item.sku] || {
            quantity: 0,
            sku: item.sku,
            desc: item.description
          }
          result[item.sku].quantity += item.quantity
          return result
        },
        {} as { [key: string]: { quantity: number; sku: string; desc: string } }
      )
    )
    const sparkleNeed5Skus = map(filter(WOMEN_8T3_MENUS, { sparkleNeed: 5 }), 'sku')
    const sparkleNeed2Skus = map(filter(WOMEN_8T3_MENUS, { sparkleNeed: 2 }), 'sku')
    const sparkleNeed1Skus = map(filter(WOMEN_8T3_MENUS, { sparkleNeed: 1 }), 'sku')

    discountNum = reduce(
      cumulatedItems,
      (result, item) => {
        if (sparkleNeed5Skus.includes(item.sku)) {
          return result + Math.floor(item.quantity / 5)
        }
        if (sparkleNeed2Skus.includes(item.sku)) {
          return result + Math.floor(item.quantity / 2)
        }
        if (sparkleNeed1Skus.includes(item.sku)) {
          return result + item.quantity
        }
        return result
      },
      0
    )
  }

  return {
    isValid:
      isAfter(deliverDeadline, WOMEN_8T3_TIME_FRAME.start) &&
      isBefore(deliverDeadline, WOMEN_8T3_TIME_FRAME.end) &&
      discountNum >= 1,
    giftNum: discountNum,
    giftText: `${giftSku} ${giftDesc}`,
    giftSku,
    saleProgram: `Tặng ${giftDesc}`
  }
}

// ============================================
// WOMEN 8/3 BUSINESS SET WHOLESALE DISCOUNT
// Chiết khấu Set DN 8/3 theo tổng giá trị
// ============================================

const BUSINESS_SET_VALUE_TO_PERCENT: { [key: number]: number } = {
  1: 10, // 1 triệu
  5: 20, // 5 triệu
  10: 25, // 10 triệu
  20: 30, // 20 triệu
  30: 35 // 30 triệu
}

/**
 * Lấy danh sách SKUs của Set DN 8/3
 */
export function getWomen8t3BusinessSetSkus(): string[] {
  return map(filter(WOMEN_8T3_MENUS, { concept: 'Set quà DN' }), 'sku')
}

/**
 * Tính chiết khấu Set DN 8/3 cho đơn hàng SHOP (không áp dụng cho onlineOrder)
 * https://chat.4handy.vn/team4handy/pl/sfuqjkjbajfa3gtbp9pj31pcey
 */
export function processWomen8t3BusinessSetWholesale({
  items,
  deadline,
  resultMap,
  orderPlatform,
  orderType
}: {
  items: IOrderItem[]
  deadline: Date
  resultMap: { [key: string]: { id: string; discount: number; saleProgram: string; noLoyalty?: boolean } }
  orderPlatform: string
  orderType: string
}): void {
  // Chỉ áp dụng đến hết ngày 8/3
  if (isAfter(deadline, endOfDay(new Date('2026-03-08')))) {
    return
  }

  const businessSetSkus = getWomen8t3BusinessSetSkus()

  // Tính tổng giá trị các sản phẩm Set DN
  const totalTargetItemsValue = sumBy(items, item => {
    if (includes(businessSetSkus, item.sku)) {
      const soldPrice = getItemSoldPrice({
        item,
        orderPlatform: orderPlatform as any,
        orderType: orderType as any
      })
      return soldPrice * item.quantity
    }
    return 0
  })

  // Tìm mức chiết khấu phù hợp
  const availableSaleValues = filter(Object.keys(BUSINESS_SET_VALUE_TO_PERCENT), key => {
    return totalTargetItemsValue / 1000000 >= Number(key)
  })

  if (!availableSaleValues.length) {
    return
  }

  const sortedAvailableSaleValues = orderBy(availableSaleValues, [value => Number(value)], ['desc'])
  const validSaleValue = head(sortedAvailableSaleValues)
  const discountPercentage = BUSINESS_SET_VALUE_TO_PERCENT[Number(validSaleValue)]

  if (!discountPercentage) {
    return
  }

  // Áp dụng chiết khấu cho từng item Set DN
  items.forEach(item => {
    if (includes(businessSetSkus, item.sku)) {
      const soldPrice = getItemSoldPrice({
        item,
        orderPlatform: orderPlatform as any,
        orderType: orderType as any
      })
      resultMap[item.id].discount = (soldPrice * item.quantity * discountPercentage) / 100
      resultMap[item.id].saleProgram = `Giảm giá Chiết khấu Set DN 8/3 (${discountPercentage}%) khi mua tổng từ ${validSaleValue} triệu`
    }
  })
}
